import {showError, AuthData, putAll} from '@fl/cmsch-fe-library';
import {SagaIterator} from 'redux-saga';
import {call, put, select} from 'typed-redux-saga';

import {Api} from 'api/gen/Api';
import {Language} from 'api/gen/Language';
import {Config} from 'app/config';
import * as routerActions from 'app/router';
import {logger} from 'app/sentry-logger';
import {State} from 'app/setup';
import {history} from 'app/setup/history';
import {LANGUAGES, t} from 'app/translations';
import {userRoutePaths} from 'app/user/routes/route-paths';
import {languageStore} from 'utils/language-store';
import {showBeError} from 'utils/show-be-error';

import {userAction} from '../action';
import {LegacyUserApi} from '../legacy-api';

export interface LocationState {
    redirectTo?: string;
}

export const buildLoginRouteState = (_state: State): LocationState =>
    ({redirectTo: `${history.location.pathname}${history.location.search}`});

function* legacyLogin(authData: AuthData): SagaIterator {
    const response = yield* call(LegacyUserApi.loginLegacy, authData);

    if (!response.isSuccess || response.data !== '') {
        yield* put(showError(
            'Přihlášení do původní aplikace',
            'Nepodařilo se přihlásit do původní aplikace.',
        ));
    }
}

export function* setLegacyLanguageCookie(language: Language): SagaIterator {
    const response = yield* call(
        Api.setLegacyLanguageCookie,
        {locale: LANGUAGES[language].legacyBeTag},
    );

    if (!response.isSuccess || response.data !== '') {
        yield* put(showError(
            'Nastavení jazyku pro iframe',
            'Nepodařilo se nastavit jazyk pro iframe',
        ));
    }
}

function* afterLegacyBeLoginSetup(showErrorIfExists: boolean): SagaIterator {
    const response = yield* call(Api.userIsLoggedLegacy);
    if (!response.isSuccess && showErrorIfExists) {
        yield putAll(showBeError(response, t('user')('loadCurrentUser')));
    }
}

function* legacyLoginAndSetLanguageCookie(
    language: Language,
    showErrorIfExists: boolean,
    auth?: AuthData,
): SagaIterator {
    if (!Config.disableLegacyLogin && auth) {
        yield* call(legacyLogin, auth);
        yield* call(afterLegacyBeLoginSetup, showErrorIfExists);
    }
    yield* call(setLegacyLanguageCookie, language);
}

export function* afterBeLoginSetup(showErrorIfExists: boolean, auth?: AuthData): SagaIterator {
    const response = yield* call(Api.getPrincipal);
    if (response.isSuccess) {
        const user = response.data;
        yield* put(userAction.getCurrentUserSuccess(user));
        logger.setUser(user.username);
        yield* call(legacyLoginAndSetLanguageCookie, user.language, showErrorIfExists, auth);

        languageStore.set(user.language);
        yield* put(userAction.setUiLanguage(user.language));
    } else if (showErrorIfExists) {
        yield putAll(showBeError(response, t('user')('loadCurrentUser')));
        const {location} = history;

        if (location.pathname === userRoutePaths.login) return;

        const routeState = yield* select(buildLoginRouteState);

        yield* put(routerActions.push(userRoutePaths.login, routeState));
    }
}
